/**
 * /coach/programs — Program Library
 *
 * Browse view + builder modal in one page (mirrors WorkoutsList simplicity).
 * Each program is a Weeks × Days grid where each cell points to a workout
 * template (or is a rest day).
 *
 *   • Create, edit, clone, soft-delete
 *   • Tier-gating (free / foundations / accelerator / elite)
 *   • Assign athletes (multi-select)
 *   • "Copy week to all remaining weeks" shortcut
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowLeft, Plus, Loader2, Copy, Trash2, Pencil, ChevronRight,
    Calendar, X, Users, Save, ChevronDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, CATEGORY_LABELS } from "@/lib/workoutTaxonomy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
    { value: "subscribed", label: "Subscribed", help: "Any active subscriber sees this automatically." },
    { value: "subscribed_plus", label: "Subscribed+", help: "Only athletes you manually assign get access." },
];

const DIFFICULTIES = ["beginner", "intermediate", "advanced"];

export default function ProgramsList() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(true);
    const [editing, setEditing] = useState(null); // program object or {new:true}

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (!["owner", "coach"].includes(user.role)) navigate("/dashboard", { replace: true });
    }, [loading, user, navigate]);

    const refresh = async () => {
        setBusy(true);
        try {
            const r = await axios.get(`${API}/coach/programs`, { withCredentials: true });
            setRows(r.data || []);
        } catch (e) {
            toast.error("Couldn't load programs", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (user?.role === "owner" || user?.role === "coach") refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.title}"?`)) return;
        try {
            await axios.delete(`${API}/coach/programs/${p.program_id}`, { withCredentials: true });
            toast.success("Deleted");
            refresh();
        } catch (e) {
            toast.error("Couldn't delete", { description: e?.response?.data?.detail });
        }
    };

    const clone = async (p) => {
        try {
            const r = await axios.post(`${API}/coach/programs/${p.program_id}/clone`, {}, { withCredentials: true });
            toast.success("Cloned");
            setEditing(r.data);
            refresh();
        } catch (e) {
            toast.error("Couldn't clone", { description: e?.response?.data?.detail });
        }
    };

    const openEdit = async (p) => {
        // re-fetch fresh to get hydrated workout_titles
        try {
            const r = await axios.get(`${API}/coach/programs/${p.program_id}`, { withCredentials: true });
            setEditing(r.data);
        } catch (e) {
            toast.error("Couldn't open", { description: e?.response?.data?.detail });
        }
    };

    if (loading || !user) return null;

    return (
        <div data-testid="programs-list" className="bg-brand-obsidian min-h-screen">
            <section className="pt-32 pb-8 border-b border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <Link to="/coach" className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5 mb-5">
                        <ArrowLeft className="w-3 h-3" /> Coach console
                    </Link>
                    <div className="grid md:grid-cols-12 gap-6 items-end">
                        <div className="md:col-span-8">
                            <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-3">
                                <span className="inline-block w-10 h-px bg-brand-red" /> Programming
                            </div>
                            <h1 className="font-display text-5xl md:text-6xl tracking-wider text-white">
                                PROGRAM <span className="text-brand-red">LIBRARY.</span>
                            </h1>
                            <p className="mt-3 text-sm text-zinc-300 max-w-xl">
                                Multi-week arcs that reference your existing workouts. Build once, assign to many.
                            </p>
                        </div>
                        <div className="md:col-span-4 flex md:justify-end">
                            <button
                                type="button"
                                data-testid="new-program"
                                onClick={() => setEditing({ _new: true, title: "", description: "", duration_weeks: 4, difficulty: "intermediate", tier_required: "subscribed", weeks: [], tags: [], subtabs: [] })}
                                className="btn-tactical text-xs"
                            >
                                <Plus className="w-4 h-4" /> New program
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-8">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    {busy ? (
                        <div className="py-16 flex items-center justify-center"><Loader2 className="w-6 h-6 text-brand-red animate-spin" /></div>
                    ) : rows.length === 0 ? (
                        <div className="border border-zinc-900 bg-zinc-950 p-10 text-center">
                            <Calendar className="w-7 h-7 text-zinc-200 mx-auto mb-3" />
                            <div className="font-display text-2xl tracking-wider text-white">No programs yet</div>
                            <div className="text-sm text-zinc-300 mt-2">Build a multi-week plan — assign it to your athletes.</div>
                        </div>
                    ) : (
                        <div className="border border-zinc-900">
                            {rows.map((p) => (
                                <div
                                    key={p.program_id}
                                    data-testid={`program-row-${p.program_id}`}
                                    onClick={() => openEdit(p)}
                                    className="px-5 py-4 flex items-center justify-between gap-4 border-b border-zinc-900 last:border-b-0 bg-zinc-950 hover:bg-zinc-900/40 transition-colors group cursor-pointer"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            {p.category && (
                                                <span className="text-[10px] tracking-[0.25em] uppercase text-brand-red">{CATEGORY_LABELS[p.category] || p.category}</span>
                                            )}
                                            <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-400">· {(TIERS.find((t) => t.value === p.tier_required) || { label: p.tier_required }).label}</span>
                                        </div>
                                        <div className="font-display text-2xl tracking-wider text-white group-hover:text-brand-red transition-colors">{p.title}</div>
                                        <div className="text-xs text-zinc-300 flex items-center gap-3 mt-1.5">
                                            <span>{p.duration_weeks}w</span>
                                            <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {p.assignment_count || 0} assigned</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                        <button
                                            type="button"
                                            data-testid={`clone-program-${p.program_id}`}
                                            onClick={(e) => { e.stopPropagation(); clone(p); }}
                                            className="text-zinc-300 hover:text-brand-red text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5"
                                        ><Copy className="w-3.5 h-3.5" /> Clone</button>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); remove(p); }}
                                            className="text-zinc-300 hover:text-brand-red text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5"
                                        ><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                                        <Pencil className="w-4 h-4 text-zinc-200 group-hover:text-brand-red transition-colors" />
                                        <ChevronRight className="w-4 h-4 text-zinc-200" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {editing && (
                <ProgramBuilder
                    program={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); refresh(); }}
                />
            )}
        </div>
    );
}


function ProgramBuilder({ program, onClose, onSaved }) {
    const isNew = program._new === true;
    const [title, setTitle] = useState(program.title || "");
    const [description, setDescription] = useState(program.description || "");
    const [category, setCategory] = useState(program.category || "");
    const [bodyFocus, setBodyFocus] = useState(program.body_focus || "");
    const [difficulty, setDifficulty] = useState(program.difficulty || "intermediate");
    const [durationWeeks, setDurationWeeks] = useState(program.duration_weeks || 4);
    // Days per week is fixed at 7 — a program week is always a full week with
    // some days as Rest, some with workouts, some with multiple stacked workouts.
    const DAYS_PER_WEEK = 7;
    const [tierRequired, setTierRequired] = useState(program.tier_required || "subscribed");
    const [tags, setTags] = useState((program.tags || []).join(", "));
    const [weeks, setWeeks] = useState(program.weeks || []);
    const [saving, setSaving] = useState(false);
    const [workoutOptions, setWorkoutOptions] = useState([]);
    const [editingCell, setEditingCell] = useState(null); // {weekIdx, dayIdx}
    const [showAssign, setShowAssign] = useState(false);
    const [activeRoster, setActiveRoster] = useState([]);

    // Load current-on-program roster
    useEffect(() => {
        if (isNew || !program.program_id) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/programs/${program.program_id}/assignments`, { withCredentials: true });
                if (cancelled) return;
                const active = (r.data?.assignments || []).filter((a) => a.status === "active");
                // Hydrate with current_week/day from /coach/programs/active
                const act = await axios.get(`${API}/coach/programs/active`, { withCredentials: true });
                const byUser = new Map((act.data?.assignments || []).filter((x) => x.program_id === program.program_id).map((x) => [x.user_id, x]));
                setActiveRoster(active.map((a) => ({ ...a, ...(byUser.get(a.user_id) || {}) })));
            } catch {/* silent */}
        })();
        return () => { cancelled = true; };
    }, [isNew, program.program_id, showAssign]);

    // Hydrate weeks to match duration_weeks × 7 days, preserving any
    // existing day data. New days start with empty workout_ids list.
    useEffect(() => {
        // Guard against mid-typing empty string — only reshape weeks when
        // the input has settled on a valid integer in range.
        const weeksNum = parseInt(String(durationWeeks), 10);
        if (Number.isNaN(weeksNum) || weeksNum < 1) return;
        setWeeks((prev) => {
            const next = [];
            for (let w = 1; w <= weeksNum; w++) {
                const existing = prev.find((x) => x.week_number === w) || { week_number: w, week_name: "", days: [], notes: "" };
                const days = [];
                for (let d = 1; d <= DAYS_PER_WEEK; d++) {
                    const exDay = (existing.days || []).find((x) => x.day_number === d);
                    if (exDay) {
                        // Backfill: if legacy single workout_id is set but list is empty, mirror it
                        const ids = (exDay.workout_ids && exDay.workout_ids.length > 0)
                            ? exDay.workout_ids
                            : (exDay.workout_id ? [exDay.workout_id] : []);
                        const titles = (exDay.workout_titles && exDay.workout_titles.length > 0)
                            ? exDay.workout_titles
                            : (exDay.workout_title ? [exDay.workout_title] : []);
                        days.push({ ...exDay, workout_ids: ids, workout_titles: titles });
                    } else {
                        days.push({
                            day_number: d, day_name: "",
                            workout_ids: [], workout_titles: [],
                            is_rest_day: false, notes: "",
                        });
                    }
                }
                next.push({ ...existing, days });
            }
            return next;
        });
    }, [durationWeeks]);

    // Load workouts for picker
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/workouts`, { withCredentials: true });
                if (!cancelled) setWorkoutOptions(r.data || []);
            } catch {/* silent */}
        })();
        return () => { cancelled = true; };
    }, []);

    const save = async () => {
        if (!title.trim()) {
            toast.error("Title required");
            return;
        }
        // Coerce duration to a valid integer between 1 and 12 in case the
        // field is mid-edit when the coach hits Save.
        const safeWeeks = Math.max(1, Math.min(12, parseInt(String(durationWeeks), 10) || 1));
        setSaving(true);
        const payload = {
            title: title.trim(),
            description: description.trim(),
            category: category || null,
            body_focus: category === "strength" && bodyFocus ? bodyFocus : null,
            difficulty,
            duration_weeks: safeWeeks,
            tier_required: tierRequired,
            tags: tags.split(",").map((s) => s.trim()).filter(Boolean),
            subtabs: [],
            weeks,
        };
        try {
            if (isNew) {
                const r = await axios.post(`${API}/coach/programs`, payload, { withCredentials: true });
                toast.success("Program created", { description: r.data?.title });
            } else {
                await axios.put(`${API}/coach/programs/${program.program_id}`, payload, { withCredentials: true });
                toast.success("Saved");
            }
            onSaved?.();
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail });
        } finally {
            setSaving(false);
        }
    };

    const setCell = (wIdx, dIdx, patch) => {
        setWeeks((prev) => prev.map((w, wi) => wi !== wIdx ? w : ({
            ...w,
            days: w.days.map((d, di) => di !== dIdx ? d : { ...d, ...patch }),
        })));
    };

    const copyWeekForward = (sourceIdx) => {
        if (!window.confirm("Copy this week's day layout to ALL remaining weeks?")) return;
        setWeeks((prev) => {
            const src = prev[sourceIdx];
            return prev.map((w, idx) => idx <= sourceIdx ? w : ({
                ...w,
                days: src.days.map((d) => ({ ...d, day_number: d.day_number })),
            }));
        });
        toast.success("Copied forward");
    };

    return (
        <div data-testid="program-builder" className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-5xl my-8">
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-950 z-10">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">{isNew ? "New" : "Edit"} program</div>
                        <h3 className="font-display text-2xl tracking-wider uppercase mt-0.5">{title || "Untitled program"}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                        {!isNew && (
                            <button
                                type="button"
                                data-testid="open-assign"
                                onClick={() => setShowAssign(true)}
                                className="btn-ghost-tactical text-xs"
                            ><Users className="w-3.5 h-3.5" /> Assign</button>
                        )}
                        <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {/* Meta */}
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Title *">
                            <input data-testid="program-title" value={title} onChange={(e) => setTitle(e.target.value)} className={inp} />
                        </Field>
                        <Field label="Access tier">
                            <select data-testid="program-tier" value={tierRequired} onChange={(e) => setTierRequired(e.target.value)} className={inp}>
                                {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                            <div className="text-[10px] text-zinc-500 mt-1.5 leading-relaxed">
                                {(TIERS.find((t) => t.value === tierRequired) || {}).help}
                            </div>
                        </Field>
                        <Field label="Category">
                            <select value={category} onChange={(e) => setCategory(e.target.value)} className={inp}>
                                <option value="">—</option>
                                {(CATEGORIES || []).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </Field>
                        {category === "strength" && (
                            <Field label="Body focus">
                                <select
                                    value={bodyFocus}
                                    onChange={(e) => setBodyFocus(e.target.value)}
                                    className={inp}
                                    data-testid="program-body-focus"
                                >
                                    <option value="">— Unspecified (mixed)</option>
                                    <option value="full_body">Full Body</option>
                                    <option value="lower_body">Lower Body</option>
                                    <option value="upper_body">Upper Body</option>
                                </select>
                                <div className="text-[10px] text-zinc-500 mt-1.5">
                                    Drives the Full/Lower/Upper sub-tabs on the athlete Strength library.
                                </div>
                            </Field>
                        )}
                        <Field label="Difficulty">
                            <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className={inp}>
                                {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </Field>
                        <Field label="Duration (weeks)">
                            <input
                                type="number"
                                min={1}
                                max={12}
                                step={1}
                                value={durationWeeks}
                                // Allow any intermediate value while typing so backspace + retype works.
                                // Clamp only on blur so 1-2 digit transitions don't trap the user.
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw === "") { setDurationWeeks(""); return; }
                                    const n = parseInt(raw, 10);
                                    if (Number.isNaN(n)) return;
                                    setDurationWeeks(n);
                                }}
                                onBlur={() => {
                                    const n = parseInt(String(durationWeeks), 10);
                                    if (Number.isNaN(n) || n < 1) setDurationWeeks(1);
                                    else if (n > 12) setDurationWeeks(12);
                                }}
                                data-testid="program-weeks"
                                className={inp}
                            />
                            <div className="text-[10px] text-zinc-500 mt-1.5">1–12 weeks</div>
                        </Field>
                        <Field label="Tags (comma-sep)">
                            <input value={tags} onChange={(e) => setTags(e.target.value)} className={inp} placeholder="off-season, lacrosse" />
                        </Field>
                        <Field label="Description">
                            <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className={inp} />
                        </Field>
                    </div>

                    {/* Mini roster — only when editing existing */}
                    {!isNew && activeRoster.length > 0 && (
                        <div data-testid="program-roster" className="border border-zinc-800 rounded-xl bg-zinc-900/30 p-4">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-2 inline-flex items-center gap-2">
                                <Users className="w-3 h-3" /> On this program ({activeRoster.length})
                            </div>
                            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {activeRoster.map((a) => (
                                    <li key={a.assignment_id} className="text-xs flex items-center justify-between gap-2 border border-zinc-800 rounded-lg px-3 py-2 bg-black/30">
                                        <div className="min-w-0">
                                            <div className="text-white truncate">{a.user_name}</div>
                                            <div className="text-[10px] text-zinc-400">
                                                {a.is_completed ? "Completed" : a.started ? `Week ${a.current_week} · Day ${a.current_day}` : "Not started"}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Weeks grid */}
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-3">Schedule</div>
                        <div className="space-y-4">
                            {weeks.map((w, wIdx) => (
                                <div key={w.week_number} className="border border-zinc-800 rounded-xl bg-zinc-900/30 p-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-200">Week {w.week_number}</div>
                                        <button
                                            type="button"
                                            onClick={() => copyWeekForward(wIdx)}
                                            data-testid={`copy-week-${w.week_number}`}
                                            className="text-[10px] tracking-[0.15em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1"
                                        ><Copy className="w-3 h-3" /> Copy forward</button>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                                        {w.days.map((d, dIdx) => {
                                            const titles = d.workout_titles && d.workout_titles.length
                                                ? d.workout_titles
                                                : (d.workout_title ? [d.workout_title] : []);
                                            const hasAnyWorkout = (d.workout_ids && d.workout_ids.length > 0) || !!d.workout_id;
                                            return (
                                                <button
                                                    key={d.day_number}
                                                    type="button"
                                                    data-testid={`day-cell-${w.week_number}-${d.day_number}`}
                                                    onClick={() => setEditingCell({ wIdx, dIdx })}
                                                    className={`border rounded-lg p-2 text-left min-h-[78px] flex flex-col justify-between transition-colors ${
                                                        d.is_rest_day
                                                            ? "border-zinc-700 bg-zinc-900/40"
                                                            : hasAnyWorkout
                                                                ? "border-brand-red/50 bg-brand-red/5 hover:bg-brand-red/10"
                                                                : "border-dashed border-zinc-700 hover:border-zinc-500"
                                                    }`}
                                                >
                                                    <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-400">Day {d.day_number}</div>
                                                    {d.is_rest_day ? (
                                                        <div className="text-xs text-zinc-300 mt-1">Rest day</div>
                                                    ) : hasAnyWorkout ? (
                                                        <div className="mt-1 space-y-0.5">
                                                            {titles.map((t, ti) => (
                                                                <div key={ti} className="text-xs text-white truncate" title={t}>• {t || "Workout"}</div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="text-xs text-zinc-500 italic mt-1">Empty</div>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-zinc-800 sticky bottom-0 bg-zinc-950 flex justify-end gap-2">
                    <button type="button" onClick={onClose} disabled={saving} className="btn-ghost-tactical text-xs">Cancel</button>
                    <button
                        type="button"
                        data-testid="save-program"
                        onClick={save}
                        disabled={saving}
                        className="btn-tactical text-xs"
                    >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        {isNew ? "Create program" : "Save"}
                    </button>
                </div>
            </div>

            {editingCell !== null && (
                <DayCellEditor
                    day={weeks[editingCell.wIdx].days[editingCell.dIdx]}
                    weekNumber={weeks[editingCell.wIdx].week_number}
                    workouts={workoutOptions}
                    onClose={() => setEditingCell(null)}
                    onSave={(patch) => { setCell(editingCell.wIdx, editingCell.dIdx, patch); setEditingCell(null); }}
                />
            )}
            {showAssign && (
                <AssignModal
                    programId={program.program_id}
                    onClose={() => setShowAssign(false)}
                />
            )}
        </div>
    );
}


function DayCellEditor({ day, weekNumber, workouts, onClose, onSave }) {
    // Normalize legacy single-slot data on entry
    const initialIds = (day.workout_ids && day.workout_ids.length > 0)
        ? [...day.workout_ids]
        : (day.workout_id ? [day.workout_id] : []);
    const [mode, setMode] = useState(day.is_rest_day ? "rest" : initialIds.length > 0 ? "workout" : "empty");
    const [workoutIds, setWorkoutIds] = useState(initialIds);
    const [dayName, setDayName] = useState(day.day_name || "");
    const [notes, setNotes] = useState(day.notes || "");

    const setSlot = (idx, id) => {
        setWorkoutIds((prev) => {
            const next = [...prev];
            next[idx] = id;
            return next;
        });
    };
    const addSlot = () => setWorkoutIds((prev) => [...prev, ""]);
    const removeSlot = (idx) => setWorkoutIds((prev) => prev.filter((_, i) => i !== idx));

    const submit = () => {
        if (mode === "rest") {
            onSave({
                is_rest_day: true,
                workout_id: null, workout_ids: [],
                workout_title: null, workout_titles: [],
                day_name: dayName, notes,
            });
        } else if (mode === "workout") {
            const cleanIds = workoutIds.filter(Boolean);
            const titles = cleanIds.map((id) => workouts.find((x) => x.workout_id === id)?.title || "Workout");
            onSave({
                is_rest_day: false,
                workout_id: cleanIds[0] || null,      // legacy mirror for older clients
                workout_title: titles[0] || null,     // legacy mirror
                workout_ids: cleanIds,
                workout_titles: titles,
                day_name: dayName, notes,
            });
        } else {
            onSave({
                is_rest_day: false,
                workout_id: null, workout_ids: [],
                workout_title: null, workout_titles: [],
                day_name: dayName, notes,
            });
        }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-950">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Week {weekNumber} · Day {day.day_number}</div>
                        <h4 className="font-display text-xl tracking-wider mt-0.5">Edit slot</h4>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="flex gap-2">
                        {[
                            { id: "workout", label: "Workouts" },
                            { id: "rest", label: "Rest day" },
                            { id: "empty", label: "Empty" },
                        ].map((opt) => (
                            <button
                                key={opt.id}
                                type="button"
                                data-testid={`day-mode-${opt.id}`}
                                onClick={() => setMode(opt.id)}
                                className={`flex-1 text-[11px] tracking-[0.15em] uppercase px-3 py-2 border transition-colors ${
                                    mode === opt.id ? "border-brand-red bg-brand-red/10 text-brand-red" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"
                                }`}
                            >{opt.label}</button>
                        ))}
                    </div>

                    {mode === "workout" && (
                        <div className="space-y-2">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">Workouts (stack as many as you want)</div>
                            {workoutIds.length === 0 && (
                                <div className="text-xs text-zinc-500 italic">No workouts picked yet — click + to add one.</div>
                            )}
                            {workoutIds.map((id, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                    <select
                                        data-testid={`workout-slot-${idx}`}
                                        value={id}
                                        onChange={(e) => setSlot(idx, e.target.value)}
                                        className={inp + " flex-1"}
                                    >
                                        <option value="">— pick a workout —</option>
                                        {workouts.map((w) => (
                                            <option key={w.workout_id} value={w.workout_id}>
                                                {w.title}{w.category ? ` (${w.category})` : ""}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        data-testid={`remove-workout-slot-${idx}`}
                                        onClick={() => removeSlot(idx)}
                                        className="text-zinc-300 hover:text-brand-red p-2"
                                        title="Remove this workout from the day"
                                    ><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                            ))}
                            <button
                                type="button"
                                data-testid="add-workout-slot"
                                onClick={addSlot}
                                className="text-[10px] tracking-[0.2em] uppercase border border-dashed border-zinc-700 text-zinc-200 hover:text-brand-red hover:border-brand-red px-3 py-2 inline-flex items-center gap-1.5"
                            >
                                <Plus className="w-3 h-3" /> Add another workout
                            </button>
                        </div>
                    )}

                    <Field label="Day label (optional)">
                        <input value={dayName} onChange={(e) => setDayName(e.target.value)} placeholder="e.g. Lower Body" className={inp} />
                    </Field>
                    <Field label="Notes (optional)">
                        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inp} />
                    </Field>
                </div>
                <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2 sticky bottom-0 bg-zinc-950">
                    <button type="button" onClick={onClose} className="btn-ghost-tactical text-xs">Cancel</button>
                    <button type="button" data-testid="save-day-cell" onClick={submit} className="btn-tactical text-xs">
                        <Save className="w-3.5 h-3.5" /> Save slot
                    </button>
                </div>
            </div>
        </div>
    );
}


function AssignModal({ programId, onClose }) {
    const [busy, setBusy] = useState(true);
    const [leads, setLeads] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [search, setSearch] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const [a, l] = await Promise.all([
                axios.get(`${API}/coach/programs/${programId}/assignments`, { withCredentials: true }),
                axios.get(`${API}/coach/leads`, { withCredentials: true }),
            ]);
            setAssignments(a.data?.assignments || []);
            setLeads(l.data?.leads || []);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [programId]);

    const assigned_ids = useMemo(() => new Set(assignments.filter((x) => x.status === "active").map((x) => x.user_id)), [assignments]);
    const candidates = useMemo(() => {
        const q = search.toLowerCase();
        return leads.filter((l) => !assigned_ids.has(l.user_id) && (!q || (l.name || "").toLowerCase().includes(q) || (l.email || "").toLowerCase().includes(q)));
    }, [leads, assigned_ids, search]);

    const assign = async (l) => {
        setSubmitting(true);
        try {
            await axios.post(`${API}/coach/programs/${programId}/assign`, { user_ids: [l.user_id] }, { withCredentials: true });
            toast.success(`Assigned ${l.name || l.email}`);
            await load();
        } catch (e) {
            toast.error("Couldn't assign", { description: e?.response?.data?.detail });
        } finally {
            setSubmitting(false);
        }
    };

    const unassign = async (a) => {
        try {
            await axios.post(`${API}/coach/programs/${programId}/unassign`, { user_id: a.user_id }, { withCredentials: true });
            toast.success("Removed");
            await load();
        } catch (e) {
            toast.error("Couldn't unassign", { description: e?.response?.data?.detail });
        }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-2xl">
                <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
                    <h4 className="font-display text-xl tracking-wider"><Users className="w-4 h-4 inline mr-2" /> Assign athletes</h4>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 grid sm:grid-cols-2 gap-4 max-h-[60vh]">
                    <div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">Already on this program ({assignments.filter((x) => x.status === "active").length})</div>
                        <ul className="space-y-1 max-h-[50vh] overflow-y-auto">
                            {assignments.filter((x) => x.status === "active").length === 0 ? (
                                <li className="text-xs text-zinc-500">No-one yet.</li>
                            ) : assignments.filter((x) => x.status === "active").map((a) => (
                                <li key={a.assignment_id} className="border border-zinc-800 rounded-lg px-3 py-2 flex items-center justify-between gap-2 bg-zinc-900/30">
                                    <div className="min-w-0">
                                        <div className="text-sm text-white truncate">{a.user_name}</div>
                                        <div className="text-[10px] text-zinc-400 truncate">{a.user_email}</div>
                                    </div>
                                    <button type="button" data-testid={`unassign-${a.user_id}`} onClick={() => unassign(a)} className="text-[10px] tracking-[0.15em] uppercase text-zinc-300 hover:text-brand-red">Remove</button>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">Add athletes</div>
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className={inp + " mb-2"} />
                        <ul className="space-y-1 max-h-[45vh] overflow-y-auto">
                            {busy ? (
                                <li className="text-xs text-zinc-400 inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</li>
                            ) : candidates.length === 0 ? (
                                <li className="text-xs text-zinc-500">No matches.</li>
                            ) : candidates.slice(0, 50).map((l) => (
                                <li key={l.user_id}>
                                    <button
                                        type="button"
                                        data-testid={`assign-${l.user_id}`}
                                        disabled={submitting}
                                        onClick={() => assign(l)}
                                        className="w-full text-left border border-zinc-800 rounded-lg px-3 py-2 flex items-center justify-between gap-2 hover:border-brand-red hover:bg-brand-red/5 disabled:opacity-50"
                                    >
                                        <div className="min-w-0">
                                            <div className="text-sm text-white truncate">{l.name || "—"}</div>
                                            <div className="text-[10px] text-zinc-400 truncate">{l.email}</div>
                                        </div>
                                        <Plus className="w-3.5 h-3.5 text-brand-red" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300">{label}</div>
            <div className="mt-1">{children}</div>
        </div>
    );
}

const inp = "w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red";
